import React, { useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";

import { QUIZ } from "@/theme/quizTokens";
import { useQuiz } from "@/state/useQuiz";
import { useQuizNavigation } from "@/state/useQuizNavigation";
import { useQuizContext } from "@/state/QuizContext";
import { useSubStep } from "@/state/SubStepContext";
import { useValidation } from "@/state/ValidationContext";
import { Steps, StepId } from "@/domains/quiz/StepDefinitions";
import { writeResultsStepToURL } from "@/state/urlState";
import { validateStep } from "@/domains/quiz/StepValidation";
import { GTM } from "@/services/gtm";
import { caloriesFromGrams } from "@/domains/nutrition/FoodEnergy";
import type { QuizPhase } from "./QuizStepper";

type QuizFooterProps = {
  phase: QuizPhase;
  setPhase: (phase: QuizPhase) => void;
};

export const QuizFooter: React.FC<QuizFooterProps> = ({ phase, setPhase }) => {
  const { dogs, dogsCountConfirmed, setDogsCount } = useQuiz();
  const { getDogStep, nextAllStep, prevAllStep } = useQuizNavigation();
  const { dispatch } = useQuizContext();
  const { handlers: subStepHandlers } = useSubStep();
  const { errorMessage, setError, clearError } = useValidation();

  const firstDog = dogs[0] ?? null;

  const currentStepId: StepId = firstDog
    ? (getDogStep(firstDog.id) as StepId)
    : "basic";

  const currentIndex = Steps.findIndex((s) => s.id === currentStepId);
  const ownerIndex = Steps.findIndex((s) => s.id === "owner");
  const isLastStep = currentIndex === ownerIndex;

  const allNamed = dogs.length > 0 && dogs.every((d) => !!d.form?.name?.trim());
  const isPrevDisabled = phase === "count";

  // Clear error when the screen changes
  useEffect(() => {
    clearError();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId, phase]);

  const handlePrevious = () => {
    clearError();

    if (phase === "count") return;
    if (phase === "names") {
      setPhase("count");
      return;
    }

    // Try sub-step navigation first
    if (subStepHandlers?.goPrev()) {
      return;
    }

    // At first sub-step of the first main step — back to the names screen
    if (currentIndex <= 0) {
      setPhase("names");
      return;
    }
    prevAllStep(dogs);
  };

  const handleNextOrFinish = () => {
    if (phase === "count") {
      // Confirm the current selection ("1" is pre-selected per the Figma default).
      if (!dogsCountConfirmed) setDogsCount(dogs.length);
      clearError();
      setPhase("names");
      return;
    }

    if (phase === "names") {
      if (!allNamed) {
        setError("name", "Please tell us your dog's name to continue.");
        return;
      }
      clearError();
      GTM.trackQuizStarted();
      setPhase("questions");
      return;
    }

    if (!firstDog) return;

    // Try sub-step navigation first
    if (subStepHandlers?.goNext()) {
      clearError();
      return;
    }

    // At last sub-step or no sub-steps — validate, then advance all together.
    // Owner details are shared across dogs, so validate them once (first dog)
    // and re-sync to every dog; per-dog fields are validated per dog.
    if (currentStepId === "owner") {
      const result = validateStep("owner", firstDog.form);
      if (!result.valid) {
        setError(result.field, result.message);
        return;
      }
      const ownerFields = {
        ownerName: firstDog.form?.ownerName,
        ownerEmail: firstDog.form?.ownerEmail,
        ownerPhone: firstDog.form?.ownerPhone,
        marketingOptIn: firstDog.form?.marketingOptIn,
      };
      dogs.slice(1).forEach((dog) =>
        dispatch({ type: "UPDATE_DOG_FORM", dogId: dog.id, payload: ownerFields })
      );
    } else {
      for (const dog of dogs) {
        const result = validateStep(currentStepId, dog.form);
        if (!result.valid) {
          const dogName = dog.form?.name?.trim();
          const message =
            dogs.length > 1 && dogName ? `${dogName}: ${result.message}` : result.message;
          setError(result.field, message);
          return;
        }
      }
    }

    clearError();

    // Track step completion with the step's answers (primary dog; owner
    // contact fields intentionally excluded — no PII into the dataLayer here).
    const form = firstDog.form ?? {};
    const stepAnswers: Record<string, Record<string, string | number | boolean | undefined>> = {
      basic: {
        breed: form.breed,
        age_years: form.years,
        age_months: form.months,
        sex: form.sex,
        reproduction: form.reproduction,
      },
      about: {
        weight_kg: form.weightKg,
        body_condition: form.bodyCondition,
        activity_level: form.activityLevel,
        treat_frequency: form.treatFrequency,
      },
      treat: {
        allergies: Array.isArray(form.allergies) ? form.allergies.join(",") : form.allergies,
      },
      owner: {
        marketing_opt_in: form.marketingOptIn,
        dogs_count: dogs.length,
      },
    };

    GTM.trackQuizStepCompleted({
      stepId: currentStepId,
      stepNumber: currentIndex + 1,
      dogId: firstDog.id,
      dogName: firstDog.form?.name,
      answers: stepAnswers[currentStepId],
    });

    if (isLastStep) {
      // Calculate total daily grams across all dogs
      const totalDailyGrams = dogs.reduce((sum, dog) => {
        const dailyGrams = Number(dog.form?.dailyGrams) || 0;
        return sum + dailyGrams;
      }, 0);

      // Get owner info from primary dog's form
      const primaryForm = dogs[0]?.form;

      // Track quiz completion
      GTM.trackQuizCompleted({
        numberOfDogs: dogs.length,
        totalDailyGrams,
        ownerName: primaryForm?.ownerName || undefined,
        ownerEmail: primaryForm?.ownerEmail || undefined,
        dogs: dogs.map(dog => ({
          dogId: dog.id,
          name: dog.form?.name,
          breed: dog.form?.breed,
          ageYears: typeof dog.form?.years === 'number' ? dog.form.years : undefined,
          weightKg: typeof dog.form?.weightKg === 'number' ? dog.form.weightKg : undefined,
          dailyGrams: typeof dog.form?.dailyGrams === 'number' ? dog.form.dailyGrams : undefined,
          dailyCalories: typeof dog.form?.dailyGrams === 'number' ? caloriesFromGrams(dog.form.dailyGrams) : undefined,
        })),
      });

      dispatch({ type: "SET_GLOBAL_STEP", step: "results" });
      dispatch({ type: "SET_RESULTS_STEP", step: 1 });
      writeResultsStepToURL(1);
      return;
    }

    nextAllStep(dogs);
  };

  const nextDisabled = phase === "names" && !allNamed;

  return (
    <>
      {/* Floating Error Toast */}
      {errorMessage && (
        <Box
          sx={{
            position: "fixed",
            left: "50%",
            bottom: "calc(var(--quizFooterH, 90px) + env(safe-area-inset-bottom) + 12px)",
            transform: "translateX(-50%)",
            zIndex: 101,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "#FFEBEE",
            borderRadius: "12px",
            px: 2,
            py: 1.5,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            maxWidth: { xs: "90%", sm: 400 },
            animation: "slideUp 0.3s ease-out",
            "@keyframes slideUp": {
              "0%": { opacity: 0, transform: "translateX(-50%) translateY(10px)" },
              "100%": { opacity: 1, transform: "translateX(-50%) translateY(0)" },
            },
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 20, color: QUIZ.color.error, flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: QUIZ.font.body,
              fontSize: { xs: "0.85rem", sm: "0.9rem" },
              color: QUIZ.color.error,
              fontWeight: 600,
              lineHeight: 1.3,
              flex: 1,
            }}
          >
            {errorMessage}
          </Typography>
          <IconButton
            size="small"
            onClick={clearError}
            sx={{
              p: 0.5,
              color: QUIZ.color.error,
              "&:hover": { backgroundColor: "rgba(198, 40, 40, 0.1)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}

      {/* Sticky bottom bar — blue with text Previous + brown Next pill */}
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          height: "calc(var(--quizFooterH, 70px) + env(safe-area-inset-bottom))",
          pb: "env(safe-area-inset-bottom)",
          background: QUIZ.color.blue,
          boxShadow: "0 -4px 17px rgba(0,0,0,0.14)",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", md: 667 },
            mx: "auto",
            px: { xs: "30px", md: "32px" },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxSizing: "border-box",
          }}
        >
          {/* PREVIOUS — text link */}
          <Box
            role="button"
            aria-disabled={isPrevDisabled}
            onClick={isPrevDisabled ? undefined : handlePrevious}
            sx={{
              fontFamily: QUIZ.font.body,
              fontWeight: 500,
              fontSize: { xs: "14.78px", md: "16px" },
              color: QUIZ.color.brown,
              cursor: isPrevDisabled ? "default" : "pointer",
              opacity: isPrevDisabled ? 0.4 : 1,
              px: "12px",
              py: "6px",
              userSelect: "none",
              "&:hover": { opacity: isPrevDisabled ? 0.4 : 0.7 },
            }}
          >
            Previous
          </Box>

          {/* NEXT / FINISH — brown pill */}
          <Box
            role="button"
            onClick={handleNextOrFinish}
            sx={{
              bgcolor: QUIZ.color.brown,
              color: QUIZ.color.white,
              fontFamily: QUIZ.font.button,
              fontWeight: 700,
              fontSize: "18px",
              lineHeight: 1.5,
              borderRadius: QUIZ.radius.pill,
              px: "46px",
              py: "12px",
              cursor: "pointer",
              userSelect: "none",
              whiteSpace: "nowrap",
              boxShadow: "0px 4px 3px rgba(0,0,0,0.1), 0px 2px 2px rgba(0,0,0,0.1)",
              opacity: nextDisabled ? 0.55 : 1,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: nextDisabled ? 0.55 : 0.9 },
            }}
          >
            {"Next"}
          </Box>
        </Box>
      </Box>
    </>
  );
};